import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Retorna rotas seguras e localizadas da Shopify.
 * @param {'update' | 'cart'} endpoint
 * @returns {string}
 */
function getLocalizedCartUrl(endpoint = 'update') {
  const routes =
    (typeof Theme !== 'undefined' && Theme.routes) ||
    (typeof window.routes !== 'undefined' && window.routes) ||
    {};

  if (endpoint === 'update') {
    return routes.cart_update_url || '/cart/update.js';
  }
  return routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';
}

/**
 * Gerencia cookies de desconto com atributos de segurança modernos.
 * @param {string | null} code
 */
function updateDiscountCookie(code) {
  if (code) {
    document.cookie = `discount_code=${encodeURIComponent(code)}; path=/; SameSite=Lax; Secure`;
  } else {
    document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure';
  }
}

/**
 * Sincroniza o campo oculto do formulário de checkout para persistência no envio padrão.
 * @param {string[]} codes
 * @param {HTMLElement} [context]
 */
function syncCartFormDiscount(codes, context = document) {
  const cartForm = context.querySelector('#cart-form') || document.getElementById('cart-form');
  if (!cartForm) return;

  let hiddenInput = cartForm.querySelector('input[name="discount"]');
  if (codes.length > 0) {
    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = 'discount';
      cartForm.appendChild(hiddenInput);
    }
    hiddenInput.value = codes.join(',');
  } else if (hiddenInput) {
    hiddenInput.remove();
  }
}

/**
 * Extrai elementos de uma string HTML usando <template> (muito mais rápido que DOMParser).
 * @param {string} htmlString
 * @param {string} selector
 * @returns {Element | null}
 */
function fastQueryFromHtml(htmlString, selector) {
  const template = document.createElement('template');
  template.innerHTML = htmlString.trim();
  return template.content.querySelector(selector);
}

/**
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} [cartDiscountError]
 * @property {HTMLElement} [cartDiscountErrorDiscountCode]
 * @property {HTMLElement} [cartDiscountErrorShipping]
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Aplica cupom ao carrinho com tratamento otimizado de layout e INP.
   * @param {SubmitEvent} event
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCodeInput = form.querySelector('input[name="discount"]');
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!(discountCodeInput instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCodeInput.value.trim();
    if (!discountCodeValue) return;

    const existingDiscounts = this.#existingDiscounts();
    const isAlreadyApplied = existingDiscounts.some(
      (code) => code.toLowerCase() === discountCodeValue.toLowerCase()
    );

    if (isAlreadyApplied) {
      this.#handleDiscountError('discount_code');
      return;
    }

    // Feedback imediato para o usuário (INP < 50ms)
    if (submitBtn) {
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('is-loading');
    }

    const abortController = this.#createAbortController();

    if (cartDiscountError) cartDiscountError.classList.add('hidden');
    if (cartDiscountErrorDiscountCode) cartDiscountErrorDiscountCode.classList.add('hidden');
    if (cartDiscountErrorShipping) cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(getLocalizedCartUrl('update'), {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Verificação de erro de cupom não aplicável
      const isInvalid = data.discount_codes?.some(
        (discount) => discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false
      );

      if (isInvalid) {
        // NÃO limpa o campo bruscamente para permitir que o usuário corrija erros de digitação
        this.#handleDiscountError('discount_code');
        discountCodeInput.focus();
        discountCodeInput.select();

        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (newHtml) {
        // Otimização INP: Usando template em vez de DOMParser
        const section = fastQueryFromHtml(newHtml, `#shopify-section-${this.dataset.sectionId}`);
        if (section) {
          const discountPills = section.querySelectorAll('.cart-discount__pill');
          const renderedCodes = Array.from(discountPills)
            .map((el) => (el instanceof HTMLElement ? el.dataset.discountCode : null))
            .filter(Boolean);

          const codesUnchanged =
            renderedCodes.length === existingDiscounts.length &&
            renderedCodes.every((code) => existingDiscounts.includes(code));

          const isShippingDiscount = data.discount_codes?.some(
            (discount) => discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === true
          );

          if (codesUnchanged && isShippingDiscount) {
            this.#handleDiscountError('shipping');
            discountCodeInput.focus();
            discountCodeInput.select();

            deferredPromise.resolve({
              cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
            });
            return;
          }
        }
      }

      // Persistência segura em Cookie e formulário do carrinho
      updateDiscountCookie(discountCodeValue);
      syncCartFormDiscount(allDiscountCodes, this.closest('cart-drawer, .cart') || document);

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      discountCodeInput.value = '';

      if (newHtml) {
        morphSection(this.dataset.sectionId, newHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Falha ao aplicar desconto',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      if (submitBtn) {
        submitBtn.removeAttribute('aria-busy');
        submitBtn.classList.remove('is-loading');
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Remove cupom de forma reativa e fluida.
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscount = async (event) => {
    if (event instanceof KeyboardEvent && event.key !== 'Enter') return;

    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof HTMLElement) || typeof this.dataset.sectionId !== 'string') return;

    const pill = target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.findIndex((c) => c.toLowerCase() === discountCode.toLowerCase());
    if (index === -1) return;

    // Feedback imediato na pílula removida (opacidade reduzida)
    pill.style.opacity = '0.5';
    pill.style.pointerEvents = 'none';

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(getLocalizedCartUrl('update'), {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      updateDiscountCookie(existingDiscounts.length > 0 ? existingDiscounts[0] : null);
      syncCartFormDiscount(existingDiscounts, this.closest('cart-drawer, .cart') || document);

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (data.sections?.[this.dataset.sectionId]) {
        morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });

        // Reabre o campo automaticamente sem recorrer a sessionStorage ou reload
        requestAnimationFrame(() => {
          const trigger = document.querySelector('.cart-pill-btn--coupon-trigger');
          if (trigger) window.__openCartCouponField?.(trigger);
        });
      }
    } catch (error) {
      deferredPromise.reject(error);
      pill.style.opacity = '1';
      pill.style.pointerEvents = '';

      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Falha ao remover desconto',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Exibe mensagens de erro sem quebrar o layout.
   * @param {'discount_code' | 'shipping'} type
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;

    if (cartDiscountError) cartDiscountError.classList.remove('hidden');
    if (target) target.classList.remove('hidden');

    const errorMessage =
      type === 'discount_code' ? 'Cupom inválido ou expirado' : 'Desconto não aplicável ao frete';

    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Retorna os códigos existentes no DOM.
   * @returns {string[]}
   */
  #existingDiscounts() {
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    return Array.from(discountPills)
      .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode : null))
      .filter((code) => Boolean(code));
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

/**
 * Gerenciador de UI do Carrinho: Cupom, Observações e Ciclo de Vida
 */
function initCartSummaryUI() {
  if (window.__cartSummaryUIInitialized) return;
  window.__cartSummaryUIInitialized = true;

  // 1. Abre o campo inline de cupom
  function openCouponField(triggerBtn) {
    const context = triggerBtn?.closest('.cart-actions-row') || document.querySelector('.cart-actions-row');
    if (!context) return;

    const form = context.querySelector('.cart-coupon-inline-form');
    const input = context.querySelector('.cart-coupon-input');
    const btn = triggerBtn || context.querySelector('.cart-pill-btn--coupon-trigger');

    context.classList.add('is-coupon-active');
    if (btn) btn.classList.add('hidden');
    if (form) form.classList.remove('hidden');

    // Executa foco na próxima frame de renderização (evita Layout Thrashing / CLS)
    if (input instanceof HTMLInputElement) {
      requestAnimationFrame(() => {
        input.focus();
        input.select();
      });
    }
  }

  // 2. Fecha o campo inline de cupom
  function closeCouponFields() {
    const activeRows = document.querySelectorAll('.cart-actions-row.is-coupon-active');
    activeRows.forEach((actionsRow) => {
      actionsRow.classList.remove('is-coupon-active');
      const triggerBtn = actionsRow.querySelector('.cart-pill-btn--coupon-trigger');
      const form = actionsRow.querySelector('.cart-coupon-inline-form');
      const input = actionsRow.querySelector('.cart-coupon-input');

      if (triggerBtn) triggerBtn.classList.remove('hidden');
      if (form) form.classList.add('hidden');
      if (input instanceof HTMLInputElement) input.value = '';
    });
  }

  // 3. Abre o modal nativo de observações
  function openNotesModal() {
    closeCouponFields();
    const modal = document.getElementById('cart-notes-modal');
    if (modal instanceof HTMLDialogElement && typeof modal.showModal === 'function') {
      modal.showModal();
      const textarea = modal.querySelector('#cart-notes-modal-textarea');
      if (textarea instanceof HTMLTextAreaElement) {
        requestAnimationFrame(() => {
          textarea.focus();
          const len = textarea.value.length;
          textarea.setSelectionRange(len, len);
        });
      }
    }
  }

  // 4. Fecha o modal de observações
  function closeNotesModal() {
    const modal = document.getElementById('cart-notes-modal');
    if (modal instanceof HTMLDialogElement && modal.open) {
      modal.close();
    }
    const statusEl = document.getElementById('cart-notes-status');
    if (statusEl) {
      statusEl.textContent = '';
      statusEl.className = 'cart-notes-status';
    }
  }

  // 5. Salva observações via AJAX
  async function saveCartNotes(saveBtn) {
    const modal = document.getElementById('cart-notes-modal');
    const textarea = modal?.querySelector('#cart-notes-modal-textarea') || document.getElementById('cart-notes-modal-textarea');
    const statusEl = modal?.querySelector('#cart-notes-status') || document.getElementById('cart-notes-status');
    const noteValue = textarea instanceof HTMLTextAreaElement ? textarea.value.trim() : '';

    const originalText = saveBtn ? saveBtn.textContent : '';
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.setAttribute('aria-busy', 'true');
      saveBtn.textContent = 'Salvando...';
    }

    try {
      const res = await fetch(getLocalizedCartUrl('update'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ note: noteValue }),
      });

      if (!res.ok) throw new Error('Falha na resposta da rede');

      if (statusEl) {
        statusEl.innerHTML = '<span class="status-check">✓</span> Salvo com sucesso!';
        statusEl.className = 'cart-notes-status is-success';
      }

      document.querySelectorAll('#cart-notes-badge, .cart-pill-saved-badge').forEach((badge) => {
        badge.classList.toggle('hidden', noteValue.length === 0);
      });

      setTimeout(() => {
        closeNotesModal();
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.removeAttribute('aria-busy');
          saveBtn.textContent = originalText;
        }
      }, 700);
    } catch (err) {
      if (statusEl) {
        statusEl.textContent = 'Erro ao salvar. Tente novamente.';
        statusEl.className = 'cart-notes-status is-error';
      }
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.removeAttribute('aria-busy');
        saveBtn.textContent = originalText;
      }
    }
  }

  // 6. Limpeza de notas órfãs - Executada fora do caminho crítico (Preserva LCP e CPU)
  function scheduleCleanOrphanCartNotes() {
    const hadPendingOrder = sessionStorage.getItem('shopify_order_pending');
    // Executa APENAS se havia um checkout iniciado pendente
    if (!hadPendingOrder) return;

    const cleanTask = () => {
      fetch(getLocalizedCartUrl('cart'))
        .then((res) => (res.ok ? res.json() : null))
        .then((cart) => {
          if (cart && cart.item_count === 0) {
            sessionStorage.removeItem('shopify_order_pending');
            if (cart.note) {
              fetch(getLocalizedCartUrl('update'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ note: '' }),
              }).catch(() => {});
            }
          }
        })
        .catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(cleanTask, { timeout: 4000 });
    } else {
      setTimeout(cleanTask, 2500);
    }
  }

  scheduleCleanOrphanCartNotes();

  // Fecha no clique no backdrop nativo do dialog (dispensa listener global de mouse)
  const notesModal = document.getElementById('cart-notes-modal');
  if (notesModal instanceof HTMLDialogElement) {
    notesModal.addEventListener('click', (event) => {
      const rect = notesModal.getBoundingClientRect();
      const isBackdropClick =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      if (isBackdropClick) {
        closeNotesModal();
      }
    });
  }

  // Tecla ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCouponFields();
      closeNotesModal();
    }
  });

  // Delegação global de clique otimizada
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    // Fechar campo de cupom ao clicar fora
    const activeCoupon = document.querySelector('.cart-actions-row.is-coupon-active');
    if (activeCoupon && !target.closest('.cart-actions-row') && !target.closest('#cart-notes-modal')) {
      closeCouponFields();
    }

    // A. Abrir campo de cupom
    const couponTrigger = target.closest('#cart-coupon-trigger-btn, .cart-pill-btn--coupon-trigger');
    if (couponTrigger) {
      e.preventDefault();
      openCouponField(couponTrigger);
      return;
    }

    // B. Abrir modal de Observações
    const notesTrigger = target.closest('#cart-notes-open-btn, .cart-pill-btn--notes');
    if (notesTrigger) {
      e.preventDefault();
      openNotesModal();
      return;
    }

    // C. Clique na pílula para acionar remoção
    const appliedPill = target.closest('.cart-pill-btn--applied');
    if (appliedPill) {
      const removeBtn = appliedPill.querySelector('.cart-discount__pill-remove');
      if (removeBtn && target !== removeBtn && !removeBtn.contains(target)) {
        removeBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
      return;
    }

    // D. Fechar modal de notas
    if (target.closest('#cart-notes-close-btn')) {
      e.preventDefault();
      closeNotesModal();
      return;
    }

    // E. Salvar notas
    const saveBtn = target.closest('#cart-notes-save-btn');
    if (saveBtn) {
      e.preventDefault();
      saveCartNotes(saveBtn);
      return;
    }

    // F. Disparo do Checkout
    const checkoutBtn = target.closest('#checkout, [name="checkout"]');
    if (checkoutBtn) {
      sessionStorage.setItem('shopify_order_pending', Date.now().toString());

      const pills = document.querySelectorAll('.cart-discount__pill[data-discount-code]');
      const codes = Array.from(pills)
        .map((p) => (p instanceof HTMLElement ? p.dataset.discountCode : null))
        .filter(Boolean);

      updateDiscountCookie(codes.length > 0 ? codes[0] : null);
      syncCartFormDiscount(codes);
    }
  });

  // Interfaces de acesso global
  window.__openCartCouponField = openCouponField;
  window.__closeCartCouponField = closeCouponFields;
  window.__openCartNotesModal = openNotesModal;
  window.__closeCartNotesModal = closeNotesModal;
}

initCartSummaryUI();